const COOKIE_NAME = 'mt_notify_consent';
const STORAGE_KEY = 'mt_notify_consent';
const SOUND_SRC = '/sounds/notification.wav';
const MAX_AGE = 60 * 60 * 24 * 365;
const SOUND_COOLDOWN_MS = 900;

export type NotifyConsent = 'granted' | 'denied' | null;

export const NOTIFY_CONSENT_EVENT = 'myteacher:notify-consent';

let audioEl: HTMLAudioElement | null = null;
let lastPlayedAt = 0;
let audioUnlocked = false;

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  return decodeURIComponent(match.slice(name.length + 1));
}

function writeCookie(name: string, value: string, maxAge: number): void {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

function parseConsent(raw: string | null): NotifyConsent {
  if (raw === 'granted' || raw === 'denied') return raw;
  return null;
}

export function getStoredNotifyConsent(): NotifyConsent {
  if (typeof window === 'undefined') return null;
  const fromCookie = parseConsent(readCookie(COOKIE_NAME));
  if (fromCookie) return fromCookie;
  try {
    return parseConsent(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export function setStoredNotifyConsent(value: Exclude<NotifyConsent, null>): void {
  if (typeof window === 'undefined') return;
  writeCookie(COOKIE_NAME, value, MAX_AGE);
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* private mode */
  }
  window.dispatchEvent(new CustomEvent(NOTIFY_CONSENT_EVENT, { detail: value }));
}

export function subscribeNotifyConsent(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = () => onChange();
  window.addEventListener(NOTIFY_CONSENT_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(NOTIFY_CONSENT_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}

export function getBrowserNotifyPermission(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return 'unsupported';
  }
  return Notification.permission;
}

export function canShowDesktopNotification(): boolean {
  return (
    getStoredNotifyConsent() === 'granted' && getBrowserNotifyPermission() === 'granted'
  );
}

export function canPlayNotifySound(): boolean {
  return getStoredNotifyConsent() === 'granted';
}

function ensureAudio(): HTMLAudioElement | null {
  if (typeof window === 'undefined') return null;
  if (!audioEl) {
    audioEl = new Audio(SOUND_SRC);
    audioEl.preload = 'auto';
    audioEl.volume = 0.7;
  }
  return audioEl;
}

/** Unlock autoplay after a user gesture (consent button). */
export function unlockNotifyAudio(): void {
  const el = ensureAudio();
  if (!el) return;
  audioUnlocked = true;
  const prev = el.volume;
  el.volume = 0;
  void el
    .play()
    .then(() => {
      el.pause();
      el.currentTime = 0;
      el.volume = prev;
    })
    .catch(() => {
      el.volume = prev;
    });
}

export function playNotifySound(): void {
  if (!canPlayNotifySound()) return;
  const now = Date.now();
  if (now - lastPlayedAt < SOUND_COOLDOWN_MS) return;
  lastPlayedAt = now;
  const el = ensureAudio();
  if (!el) return;
  el.currentTime = 0;
  void el.play().catch(() => {
    if (!audioUnlocked) return;
  });
}

export async function requestBrowserNotifyPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return 'unsupported';
  }
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch {
    return Notification.permission;
  }
}

export async function grantNotifyConsent(): Promise<void> {
  setStoredNotifyConsent('granted');
  await requestBrowserNotifyPermission();
  const el = ensureAudio();
  if (!el) return;
  audioUnlocked = true;
  el.volume = 0.7;
  el.currentTime = 0;
  lastPlayedAt = Date.now();
  void el.play().catch(() => undefined);
}

export function denyNotifyConsent(): void {
  setStoredNotifyConsent('denied');
}

export function announceIncoming(input: {
  kind: 'message' | 'notification';
  title: string;
  body?: string;
  skip?: boolean;
}): void {
  if (input.skip || getStoredNotifyConsent() !== 'granted') return;
  playNotifySound();
  if (!canShowDesktopNotification()) return;
  try {
    const note = new Notification(input.title, {
      body: (input.body || (input.kind === 'message' ? 'Новое сообщение' : 'Новое уведомление')).slice(
        0,
        140,
      ),
      icon: '/favicon.svg',
      silent: true,
      tag: `mt-${input.kind}`,
    });
    window.setTimeout(() => note.close(), 6000);
  } catch {
    /* Safari private / missing constructor */
  }
}
